import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../../seo/Seo';
import { softwareSchema } from '../../seo/schema';
import Icon from '../home/Icon';
import {
  HERO_LINES, HERO_TOTAL, HERO_TENDER, BILLING_MODES, SCAN_ROWS,
  TILE_CATEGORIES, TILES, POS_FEATURES, COMING_SOON, LEDGER_GROUPS, BOOKS_POINTS,
  MANAGER_POINTS, SETUP_STEPS, POS_INDUSTRIES, POS_FAQS,
} from './posData';
import './PointOfSale.css';

const inr = (n) => '₹' + n.toLocaleString('en-IN');

// Content is visible by default; JS layers a subtle entrance on top.
const useReveal = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.pos-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    const failsafe = setTimeout(() => els.forEach((el) => el.classList.add('in')), 1200);
    return () => { io.disconnect(); clearTimeout(failsafe); };
  }, []);
};

/* ─── Hero: a counter mid-bill ──────────────────────────────────────────── */

const CounterMock = () => (
  <div className="pos-mock" aria-hidden="true">
    <div className="pos-window">
      <div className="pos-window-bar">
        <span className="pos-window-crumb">Raya POS › MG Road store › Counter 2</span>
        <span className="pos-window-who"><span className="pos-live-dot" />Asha · shift open</span>
      </div>
      <div className="pos-counter">
        <div className="pos-counter-cart">
          <div className="pos-search">
            <Icon name="search" size={15} />
            <span className="pos-search-text">3*8901030</span>
            <span className="pos-caret" />
            <span className="pos-kbd">Scan or type</span>
          </div>
          <div className="pos-lines">
            {HERO_LINES.map((l) => (
              <div className={`pos-line ${l.fresh ? 'is-fresh' : ''}`} key={l.name}>
                <div className="pos-line-main">
                  <span className="pos-line-name">{l.name}</span>
                  <span className="pos-line-meta">{l.meta}</span>
                </div>
                <span className="pos-line-qty">× {l.qty}</span>
                <span className="pos-line-amt">{inr(l.qty * l.rate)}</span>
              </div>
            ))}
          </div>
          <div className="pos-customer">
            <Icon name="smartphone" size={14} />
            <span>Walk-in · <strong>98250 ••••10</strong></span>
            <span className="pos-customer-tag">No GSTIN · retail bill</span>
          </div>
        </div>
        <div className="pos-counter-pay">
          <span className="pos-pay-label">Amount due</span>
          <div className="pos-pay-total">{inr(HERO_TOTAL)}</div>
          <span className="pos-pay-sub">GST included · 4 items, 7 units</span>
          <div className="pos-tenders">
            {HERO_TENDER.map((t) => (
              <div className="pos-tender" key={t.mode}>
                <span className="pos-tender-icon"><Icon name={t.icon} size={15} /></span>
                <span className="pos-tender-mode">{t.mode}</span>
                <span className="pos-tender-note">{t.note}</span>
                <span className="pos-tender-amt">{inr(t.amount)}</span>
              </div>
            ))}
          </div>
          <div className="pos-pay-btn">Complete bill · print 80 mm</div>
        </div>
      </div>
    </div>
    <div className="pos-float pos-float--tr">
      <span className="pos-float-icon pos-float-icon--ok">✓</span>
      <span>
        <strong>Posted to the ledger</strong>
        <small>Sales · COGS · Receipt</small>
      </span>
    </div>
    <div className="pos-float pos-float--bl">
      <span className="pos-float-icon pos-float-icon--wa"><Icon name="message-circle" size={14} /></span>
      <span>
        <strong>Receipt sent on WhatsApp</strong>
        <small>Bill MG2-0418 · {inr(HERO_TOTAL)}</small>
      </span>
    </div>
  </div>
);

/* ─── The two counter layouts ───────────────────────────────────────────── */

const ScanMock = () => (
  <div className="pos-mini" aria-hidden="true">
    <div className="pos-search pos-search--sm">
      <Icon name="scan-line" size={14} />
      <span className="pos-search-text">3*8904109</span>
      <span className="pos-caret" />
      <span className="pos-kbd"><Icon name="keyboard" size={12} />Enter</span>
    </div>
    <div className="pos-lines">
      {SCAN_ROWS.map((r) => (
        <div className={`pos-line ${r.fresh ? 'is-fresh' : ''}`} key={r.name}>
          <div className="pos-line-main">
            <span className="pos-line-name">{r.name}</span>
            <span className="pos-line-meta">{r.meta}</span>
          </div>
          <span className="pos-line-qty">× {r.qty}</span>
          <span className="pos-line-amt">{r.amount}</span>
        </div>
      ))}
    </div>
  </div>
);

const TilesMock = () => (
  <div className="pos-mini pos-mini--tiles" aria-hidden="true">
    <div className="pos-rail">
      {TILE_CATEGORIES.map((c, i) => (
        <span className={`pos-rail-item ${i === 0 ? 'is-on' : ''}`} key={c}>{c}</span>
      ))}
    </div>
    <div className="pos-tiles">
      {TILES.map((t, i) => (
        <div className={`pos-tile ${i === 1 ? 'is-tapped' : ''}`} key={t.name}>
          <div className="pos-tile-pic" style={{ background: t.tint, color: t.color }}>
            <Icon name={t.icon} size={26} />
          </div>
          <span className="pos-tile-name">{t.name}</span>
          <span className="pos-tile-price">{t.price}</span>
          {i === 1 && <span className="pos-tile-count">2</span>}
        </div>
      ))}
    </div>
  </div>
);

const MODE_MOCKS = { scan: ScanMock, tiles: TilesMock };

/* ─── Page ───────────────────────────────────────────────────────────────── */

const PointOfSalePage = () => {
  const navigate = useNavigate();
  useReveal();

  return (
    <div className="pos-page">
      <Seo
        path="/point-of-sale"
        faqs={POS_FAQS}
        schema={[softwareSchema({ name: 'Raya POS', subCategory: 'Point of Sale', path: '/point-of-sale', description: 'Point of sale for Indian retail counters, connected to Raya ERP — scan-first or touch billing, split payments with UPI, GST invoices, shifts and instant posting to the books.', extra: { operatingSystem: 'Web browser (PC, Android tablet, iPad)' } })]}
      />

      {/* ── Hero ─────────────────────────────── */}
      <section className="pos-hero">
        <div className="pos-hero-grid" aria-hidden="true" />
        <div className="pos-hero-blob pos-hero-blob--blue" aria-hidden="true" />
        <div className="pos-hero-blob pos-hero-blob--amber" aria-hidden="true" />
        <div className="pos-container pos-hero-inner">
          <div className="pos-hero-text">
            <span className="pos-hero-badge"><Icon name="store" size={14} />Raya POS · for retail counters</span>
            <h1 className="pos-hero-title">Bill fast at the counter. <em>The books keep up on their own.</em></h1>
            <p className="pos-hero-sub">
              Raya POS is a counter app for Indian shops, connected to Raya ERP — the same products,
              stock, prices, customers, GST and books. Scan or tap, take cash, UPI or card, hand over the
              receipt, and the sale is already in your ledger.
            </p>
            <div className="pos-hero-actions">
              <button type="button" className="pos-btn pos-btn--primary pos-btn--lg" onClick={() => navigate('/register')}>Start free trial</button>
              <button type="button" className="pos-btn pos-btn--ghost pos-btn--lg" onClick={() => navigate('/contact-us')}>Book a demo</button>
            </div>
            <p className="pos-hero-meta">PCs · Android tablets · iPads — in the browser</p>
          </div>
          <CounterMock />
        </div>
      </section>

      {/* ── Two ways to bill ─────────────────────────────── */}
      <section className="pos-section pos-section--alt" id="layouts">
        <div className="pos-container">
          <div className="pos-head pos-head--center pos-reveal">
            <span className="pos-eyebrow">Two ways to bill</span>
            <h2 className="pos-h2">A grocery till and a café counter <em>work differently.</em></h2>
            <p className="pos-lead pos-lead--center">
              So Raya POS has two layouts. Choose one per store, or per counter when one shop needs both.
            </p>
          </div>
          <div className="pos-modes pos-reveal">
            {BILLING_MODES.map((m) => {
              const Mock = MODE_MOCKS[m.key];
              return (
                <article className="pos-mode" key={m.key}>
                  <header className="pos-mode-head">
                    <span className="pos-mode-icon"><Icon name={m.icon} size={18} /></span>
                    <div>
                      <span className="pos-mode-label">{m.label}</span>
                      <h3 className="pos-mode-title">{m.title}</h3>
                    </div>
                  </header>
                  <Mock />
                  <p className="pos-mode-blurb">{m.blurb}</p>
                  <ul className="pos-ticks">
                    {m.points.map((p) => (
                      <li key={p}><span className="pos-tick" aria-hidden="true">✓</span>{p}</li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────── */}
      <section className="pos-section">
        <div className="pos-container">
          <div className="pos-head pos-reveal">
            <span className="pos-eyebrow">At the counter</span>
            <h2 className="pos-h2">Everything a busy till needs, <em>nothing it does not.</em></h2>
            <p className="pos-lead">
              Built around the moments that slow a queue down — the customer who wants a GST bill, the
              split between UPI and cash, the refund that needs a manager.
            </p>
          </div>
          <div className="pos-features pos-reveal">
            {POS_FEATURES.map((f) => (
              <article className="pos-feature" key={f.title}>
                <span className="pos-feature-icon"><Icon name={f.icon} size={19} /></span>
                <h3 className="pos-feature-title">{f.title}</h3>
                <p className="pos-feature-desc">{f.desc}</p>
              </article>
            ))}
          </div>
          <p className="pos-soon pos-reveal">
            <span className="pos-soon-tag">Coming soon</span>
            {COMING_SOON.join(' and ')}.
          </p>
        </div>
      </section>

      {/* ── Connected to your books ─────────────────────────────── */}
      <section className="pos-section pos-section--dark" id="books">
        <div className="pos-container">
          <div className="pos-books pos-reveal">
            <div className="pos-books-text">
              <span className="pos-eyebrow pos-eyebrow--light">Connected to your books</span>
              <h2 className="pos-h2 pos-h2--light">The bill is the entry. <em>Nobody types it twice.</em></h2>
              <p className="pos-lead pos-lead--light">
                Raya POS sells from the same item master, stock and price list as Raya ERP. When a
                cashier completes a bill, finance has already happened.
              </p>
              <div className="pos-books-points">
                {BOOKS_POINTS.map((b) => (
                  <div className="pos-books-point" key={b.title}>
                    <span className="pos-books-icon"><Icon name={b.icon} size={16} /></span>
                    <div>
                      <h3 className="pos-books-title">{b.title}</h3>
                      <p className="pos-books-desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pos-ledger" aria-label="Example ledger postings for one counter bill">
              <div className="pos-ledger-head">
                <span>Bill MG2-0419 · ₹1,180.00</span>
                <span className="pos-ledger-badge">Posted instantly</span>
              </div>
              <div className="pos-ledger-cols">
                <span>Ledger</span><span>Dr</span><span>Cr</span>
              </div>
              {LEDGER_GROUPS.map((g) => (
                <div className="pos-ledger-group" key={g.label}>
                  <div className="pos-ledger-glabel">{g.label}</div>
                  {g.rows.map(([name, dr, cr]) => (
                    <div className="pos-ledger-row" key={g.label + name}>
                      <span>{name}</span><span>{dr}</span><span>{cr}</span>
                    </div>
                  ))}
                </div>
              ))}
              <div className="pos-ledger-foot">
                <Icon name="info" size={13} />
                Illustrative bill: ₹1,000 + 18% GST, paid ₹1,000 by UPI and ₹180 in cash.
              </div>
            </div>
          </div>

          <div className="pos-manager pos-reveal">
            <div className="pos-manager-text">
              <span className="pos-manager-icon"><Icon name="bar-chart-3" size={18} /></span>
              <div>
                <h3 className="pos-manager-title">The manager view lives in Raya ERP</h3>
                <p className="pos-manager-desc">Watch the counters from the back office or from home.</p>
              </div>
            </div>
            <ul className="pos-manager-list">
              {MANAGER_POINTS.map((p) => (
                <li key={p}><span className="pos-tick" aria-hidden="true">✓</span>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Set up in minutes ─────────────────────────────── */}
      <section className="pos-section">
        <div className="pos-container">
          <div className="pos-head pos-head--center pos-reveal">
            <span className="pos-eyebrow">Set up in minutes</span>
            <h2 className="pos-h2">From switched off <em>to first bill.</em></h2>
            <p className="pos-lead pos-lead--center">Your items, prices and GST rates are already in Raya ERP, so there is nothing to import.</p>
          </div>
          <ol className="pos-steps pos-reveal">
            {SETUP_STEPS.map((s) => (
              <li className="pos-step" key={s.num}>
                <div className="pos-step-top">
                  <span className="pos-step-num">{s.num}</span>
                  <span className="pos-step-icon"><Icon name={s.icon} size={18} /></span>
                </div>
                <h3 className="pos-step-title">{s.title}</h3>
                <p className="pos-step-desc">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Industries ─────────────────────────────── */}
      <section className="pos-section pos-section--alt">
        <div className="pos-container">
          <div className="pos-head pos-reveal">
            <span className="pos-eyebrow">Made for Indian retail</span>
            <h2 className="pos-h2">Built for shops that <em>bill all day.</em></h2>
          </div>
          <div className="pos-industries pos-reveal">
            {POS_INDUSTRIES.map((ind) => (
              <div className="pos-industry" key={ind.name}>
                <span className="pos-industry-icon"><Icon name={ind.icon} size={20} /></span>
                <div>
                  <h3 className="pos-industry-name">{ind.name}</h3>
                  <p className="pos-industry-desc">{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────── */}
      <section className="pos-section">
        <div className="pos-container pos-container--narrow">
          <div className="pos-head pos-head--center pos-reveal">
            <span className="pos-eyebrow">Questions</span>
            <h2 className="pos-h2">What shop owners <em>ask first.</em></h2>
          </div>
          <div className="pos-faq pos-reveal">
            {POS_FAQS.map((f) => (
              <details className="pos-faq-item" key={f.q}>
                <summary className="pos-faq-q">{f.q}<span className="pos-faq-plus" aria-hidden="true">+</span></summary>
                <p className="pos-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="pos-faq-more pos-reveal">
            Want the numbers?{' '}
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>See Raya ERP pricing →</a>
          </p>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────── */}
      <section className="pos-cta">
        <div className="pos-cta-blob" aria-hidden="true" />
        <div className="pos-container pos-cta-inner">
          <h2 className="pos-cta-title">Open your counter on Raya POS.</h2>
          <p className="pos-cta-sub">Bills at the front, stock and books in step at the back.</p>
          <div className="pos-cta-actions">
            <button type="button" className="pos-btn pos-btn--primary pos-btn--lg" onClick={() => navigate('/register')}>Start free trial</button>
            <button type="button" className="pos-btn pos-btn--outline pos-btn--lg" onClick={() => navigate('/contact-us')}>Book a demo</button>
          </div>
          <p className="pos-cta-meta">Raya Software Solutions · Built for Indian businesses</p>
        </div>
      </section>
    </div>
  );
};

export default PointOfSalePage;
